import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Bot,
  Send,
  Sparkles,
  Lightbulb,
  Calendar,
  TrendingUp,
  Clock,
  Loader2,
  RefreshCw,
  MessageSquare,
  Zap,
  Target,
  BarChart3
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const AssistantPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const messagesEndRef = useRef(null);
  
  // AI recommendations
  const [recommendations, setRecommendations] = useState([
    {
      id: 1,
      type: 'priority',
      title: 'Prioriser le projet Logo Design',
      description: 'Ce projet a une deadline proche et représente un client VIP.',
      action: 'Voir le projet',
      link: '/projects'
    },
    {
      id: 2,
      type: 'optimization',
      title: 'Optimiser la répartition des tâches',
      description: 'Jean Dupont a une charge élevée cette semaine. Réassigner 2 tâches.',
      action: 'Voir le planning',
      link: '/planning'
    },
    {
      id: 3,
      type: 'opportunity',
      title: 'Prospect chaud détecté',
      description: 'Tech Solutions a consulté 3x votre page pricing. À relancer.',
      action: 'Voir le prospect',
      link: '/prospects'
    }
  ]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const quickPrompts = [
    { icon: Calendar, text: 'Quelles sont mes priorités aujourd\'hui ?' },
    { icon: TrendingUp, text: 'Comment améliorer la productivité de l\'équipe ?' },
    { icon: Target, text: 'Quels prospects devrais-je relancer ?' },
    { icon: BarChart3, text: 'Analyse mes performances du mois' }
  ];

  const handleSend = async () => {
    if (!message.trim()) return;

    const userMessage = { role: 'user', content: message, timestamp: new Date() };
    setConversation(prev => [...prev, userMessage]);
    setMessage('');
    setLoading(true);

    // Simulate AI response - in production would call real AI API
    await new Promise(resolve => setTimeout(resolve, 1500));

    const aiResponses = [
      "D'après l'analyse de vos projets en cours, je vous recommande de vous concentrer sur :\n\n1. **Logo Design pour Tech Corp** - Deadline dans 3 jours\n2. **Site web pour Startup X** - Le client attend une mise à jour\n3. **Vidéo promotionnelle** - En attente de validation\n\nVoulez-vous que je vous aide à planifier ces tâches ?",
      "Pour améliorer la productivité de l'équipe, voici mes suggestions :\n\n• Utiliser la vue Planning pour mieux répartir la charge\n• Activer les notifications pour les deadlines\n• Planifier des points hebdomadaires courts\n\nJe peux vous aider à configurer ces éléments.",
      "Voici les prospects à relancer en priorité :\n\n1. **Tech Solutions** - A visité 3 fois le site, intéressé par le pack Premium\n2. **Agence Media** - Proposition envoyée il y a 5 jours\n3. **StartupLab** - Demande de devis en attente\n\nSouhaitez-vous que je prépare un message de relance ?",
      "Voici l'analyse de vos performances ce mois :\n\n📈 **Chiffre d'affaires** : +15% vs mois dernier\n⏱️ **Temps moyen par projet** : 12h (-2h)\n⭐ **Satisfaction client** : 4.8/5\n📊 **Projets livrés** : 8 (objectif: 10)\n\nExcellente progression ! Continuez sur cette lancée."
    ];

    const aiMessage = {
      role: 'assistant',
      content: aiResponses[Math.floor(Math.random() * aiResponses.length)],
      timestamp: new Date()
    };
    
    setConversation(prev => [...prev, aiMessage]);
    setLoading(false);
  };

  const handleQuickPrompt = (prompt) => {
    setMessage(prompt);
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'priority': return <Clock className="w-5 h-5 text-red-500" />;
      case 'optimization': return <TrendingUp className="w-5 h-5 text-blue-500" />;
      case 'opportunity': return <Zap className="w-5 h-5 text-amber-500" />;
      default: return <Lightbulb className="w-5 h-5 text-purple-500" />;
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                <Bot className="w-5 h-5 text-white" />
              </div>
              Assistant IA
            </h1>
            <p className="text-[#64748b] mt-1">
              Votre assistant intelligent pour optimiser votre travail
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat Area */}
          <Card className="card lg:col-span-2 flex flex-col" style={{ height: '600px' }}>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Conversation
              </CardTitle>
            </CardHeader>
            
            {/* Messages */}
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {conversation.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-4">
                    <Sparkles className="w-10 h-10 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1a365d] mb-2">Comment puis-je vous aider ?</h3>
                  <p className="text-sm text-[#64748b] max-w-md">
                    Je peux vous aider à organiser votre travail, analyser vos performances, 
                    et vous donner des recommandations personnalisées.
                  </p>
                  
                  {/* Quick Prompts */}
                  <div className="grid grid-cols-2 gap-2 mt-6 w-full max-w-md">
                    {quickPrompts.map((prompt, i) => {
                      const Icon = prompt.icon;
                      return (
                        <button
                          key={i}
                          onClick={() => handleQuickPrompt(prompt.text)}
                          className="flex items-center gap-2 p-3 rounded-xl border border-[#e2e8f0] hover:border-purple-300 hover:bg-purple-50 transition-all text-left"
                        >
                          <Icon className="w-4 h-4 text-purple-500 shrink-0" />
                          <span className="text-xs text-[#64748b]">{prompt.text}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
                  {conversation.map((msg, i) => (
                    <div key={i} className={cn("flex gap-3", msg.role === 'user' ? "flex-row-reverse" : "")}>
                      {msg.role === 'assistant' ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      ) : (
                        <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
                      )}
                      <div className={cn("max-w-[80%]", msg.role === 'user' && "text-right")}>
                        <div className={cn(
                          "rounded-2xl px-4 py-2.5",
                          msg.role === 'user' 
                            ? "bg-[var(--color-accent)] text-white rounded-tr-md" 
                            : "bg-[#f1f5f9] text-[#1a365d] rounded-tl-md"
                        )}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        <span className="text-xs text-[#94a3b8] mt-1 block">
                          {formatTime(msg.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-[#f1f5f9] rounded-2xl rounded-tl-md px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                          <span className="text-sm text-[#64748b]">Réflexion en cours...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </CardContent>

            {/* Input */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Posez votre question..."
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  className="flex-1"
                />
                <Button 
                  onClick={handleSend} 
                  disabled={loading || !message.trim()}
                  className="bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Recommendations */}
          <div className="space-y-6">
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  Recommandations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recommendations.map(rec => (
                  <div 
                    key={rec.id}
                    className="p-3 rounded-xl border border-[#e2e8f0] hover:shadow-md transition-all cursor-pointer"
                    onClick={() => window.location.href = rec.link}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {getRecommendationIcon(rec.type)}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-[#1a365d]">{rec.title}</h4>
                        <p className="text-xs text-[#64748b] mt-1">{rec.description}</p>
                        <Button variant="link" size="sm" className="p-0 h-auto mt-2 text-xs" style={{ color: theme.colors?.accent }}>
                          {rec.action} →
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* AI Capabilities */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-sm">Ce que je peux faire</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    'Analyser vos projets et priorités',
                    'Optimiser la répartition des tâches',
                    'Suggérer des actions commerciales',
                    'Générer des rapports de performance',
                    'Répondre à vos questions'
                  ].map((cap, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#64748b]">
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      {cap}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssistantPage;
